import { useEffect, useRef } from 'react';

interface UseAudioAnalyserProps {
    audioStream: MediaStream | undefined;
    onAnalyze?: (average: number) => void;
}

export function useAudioAnalyser({ audioStream, onAnalyze }: UseAudioAnalyserProps) {
    const animationFrameId = useRef<number | undefined>(undefined);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);

    useEffect(() => {
        if (!audioStream || audioStream.getAudioTracks().length === 0) {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            return;
        }

        const AudioContextClass =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(audioStream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const analyze = () => {
            if (!analyserRef.current) return;

            analyserRef.current.getByteFrequencyData(dataArray);

            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i];
            }
            const average = sum / bufferLength;

            onAnalyze?.(average);

            animationFrameId.current = requestAnimationFrame(analyze);
        };

        animationFrameId.current = requestAnimationFrame(analyze);

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            source.disconnect();
            analyser.disconnect();
            audioContext.close();
        };
    }, [audioStream]);
}
